import React, { useState } from "react";
import { DndContext, DragEndEvent, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor, DragOverlay } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { PageBuilderProvider, usePageBuilder } from "../../context/PageBuilderContext";
import { Canvas } from "./Canvas";
import { ComponentPalette } from "./ComponentPalette";
import { PropertyDrawer } from "./PropertyDrawer";
import { LayerTree } from "./LayerTree";
import { IComponentNode } from "../../types/pageBuilder.types";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { componentTemplates } from "./ComponentPalette";
import { LanguageSwitcher } from "../common/LanguageSwitcher";
import { ModalLanguageProvider, useModalLanguage } from "../../context/ModalLanguageContext";
import { platformApiService } from "../../services/platformApi";
import { useApi } from "../../context/ApiContext";
import { FaUndo, FaGripVertical } from "react-icons/fa";

interface PageBuilderProps {
 initialLayout?: IComponentNode | null;
 onCancel?: () => void;
 onSave?: (layout: IComponentNode) => void | Promise<void>;
 onReset?: (defaultTemplate: IComponentNode) => void | Promise<void>;
 pageName?: string;
 zoneName?: string;
 pageType?: string;
}

const PageBuilderContent: React.FC<{ isPreviewMode: boolean; setIsPreviewMode: (mode: boolean) => void; onCancel?: () => void; onSave?: (layout: IComponentNode) => void | Promise<void>; onReset?: (defaultTemplate: IComponentNode) => void | Promise<void>; pageName?: string; zoneName?: string; pageType?: string }> = ({ isPreviewMode, setIsPreviewMode, onCancel, onSave, onReset, pageName, zoneName, pageType }) => {
 const { selectedComponent, setLayout } = usePageBuilder();
 const { currentLanguage, setCurrentLanguage } = useModalLanguage();
 const { api } = useApi();
 const [isPropertyDrawerOpen, setIsPropertyDrawerOpen] = React.useState(false);
 const [shouldOpenDrawer, setShouldOpenDrawer] = React.useState(false);
 const [isResetting, setIsResetting] = React.useState(false);
 const [hasTemplateContent, setHasTemplateContent] = React.useState<boolean>(false);
 const [activeTab, setActiveTab] = React.useState<"palette" | "layers">("palette");

 // Only open drawer when explicitly requested (via edit icon), not just on selection
 React.useEffect(() => {
  if (shouldOpenDrawer && selectedComponent) {
   setIsPropertyDrawerOpen(true);
   setShouldOpenDrawer(false);
  } else if (!selectedComponent) {
   setIsPropertyDrawerOpen(false);
  }
 }, [selectedComponent, shouldOpenDrawer]);

 // Expose function to open drawer via context or prop
 React.useEffect(() => {
  // Listen for custom event to open drawer
  const handleOpenDrawer = () => {
   setShouldOpenDrawer(true);
  };
  window.addEventListener('pagebuilder:openDrawer', handleOpenDrawer);
  return () => {
   window.removeEventListener('pagebuilder:openDrawer', handleOpenDrawer);
  };
 }, []);

 // Check if default template exists and has content
 React.useEffect(() => {
  const checkTemplate = async () => {
    if (pageType === "static" && pageName) {
      // For static pages, check if template exists (backend already verifies it has content)
      try {
        const response = await api.get(`/page-builder/pages/${pageName}/default-template-exists`);
        const exists = response.data?.exists || false;
        setHasTemplateContent(exists); // Backend only returns true if template has content
      } catch (error: any) {
        console.warn("Error checking default template:", error);
        setHasTemplateContent(false);
      }
    } else if (pageType === "platform" && zoneName && pageName) {
      // For platform zones, check if template has content
      try {
        const response = await platformApiService.getDefaultZoneTemplate(pageName, zoneName);
        const defaultTemplate = response.data?.zone;
        const hasContent = defaultTemplate && defaultTemplate.children && defaultTemplate.children.length > 0;
        setHasTemplateContent(hasContent);
      } catch (error: any) {
        console.warn("Error checking platform zone template:", error);
        setHasTemplateContent(false);
      }
    } else {
      setHasTemplateContent(false);
    }
  };
  checkTemplate();
 }, [pageType, pageName, zoneName, api]);

 return (
  <div 
   className="fixed inset-0 flex bg-gray-100 z-50"
   onClick={(e) => {
    // Stop all click events from bubbling to parent form elements
    // Only stop propagation, not default behavior, to allow normal interactions
    e.stopPropagation();
   }}
   onMouseDown={(e) => {
    // Also stop mousedown to prevent any form interactions
    e.stopPropagation();
   }}
   role="dialog"
   aria-modal="true"
  >
   {/* Left Sidebar - Component Palette and Layer Tree - Only show in edit mode */}
   {!isPreviewMode && (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
     {/* Tabs for switching between Palette and Layers */}
     <div className="flex border-b border-gray-200">
      <button
       type="button"
       onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveTab("palette");
       }}
       className={`flex-1 px-4 py-2 text-sm font-medium ${
        activeTab === "palette"
         ? "text-blue-600 border-b-2 border-blue-600"
         : "text-gray-600 hover:text-gray-900"
       }`}
      >
       Components
      </button>
      <button
       type="button"
       onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveTab("layers");
       }}
       className={`flex-1 px-4 py-2 text-sm font-medium ${
        activeTab === "layers"
         ? "text-blue-600 border-b-2 border-blue-600"
         : "text-gray-600 hover:text-gray-900"
       }`}
      >
       Layers
      </button>
     </div>
     {/* Content based on active tab */}
     <div className="flex-1 overflow-hidden">
      {activeTab === "palette" ? (
       <div className="h-full overflow-y-auto">
        <ComponentPalette />
       </div>
      ) : (
       <div className="h-full">
        <LayerTree />
       </div>
      )}
     </div>
    </div>
   )}

   {/* Main Canvas Area */}
   <div className="flex-1 flex flex-col overflow-hidden">
    {/* Toolbar */}
    <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
     <div className="flex items-center gap-4 flex-1">
      {(pageName || zoneName || pageType) && (
       <div className="flex items-center gap-2 text-sm text-gray-600">
        {pageName && (
         <span className="font-medium text-gray-900">{pageName}</span>
        )}
        {zoneName && (
         <>
          <span className="text-gray-400">/</span>
          <span className="font-medium text-gray-900">Zone: {zoneName}</span>
         </>
        )}
        {pageType && (
         <>
          <span className="text-gray-400">/</span>
          <span className="text-gray-500">Type: {pageType}</span>
         </>
        )}
       </div>
      )}
      <div className="flex items-center gap-2">
       <LanguageSwitcher
        currentLanguage={currentLanguage}
        onLanguageChange={(lang) => {
         setCurrentLanguage(lang);
         // Don't change browser language - only editing context
        }}
        showLabel={true}
        className="mr-2"
       />
       <Button
        type="button"
        onClick={(e) => {
         e.preventDefault();
         e.stopPropagation();
         setIsPreviewMode(!isPreviewMode);
        }}
        variant={isPreviewMode ? "primary" : "secondary"}
        size="sm"
       >
        {isPreviewMode ? "Edit" : "Preview"}
       </Button>
       {onSave && (
        <SaveButtonWrapper onSave={onSave} />
       )}
       {onCancel && (
        <Button
         type="button"
         onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCancel();
         }}
         variant="secondary"
         size="sm"
        >
         Close
        </Button>
       )}
       {/* Reset button - only show if template exists and has content */}
       {hasTemplateContent ? (
        <Button
         type="button"
         onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (pageType === "platform" && zoneName) {
           // Platform zone reset
           // pageName MUST be provided - it's the page slug (e.g., "home")
           // zoneName is the zone name (e.g., "hero")
           if (!pageName) {
            alert(`Unable to reset zone: Page slug is missing. Please ensure you're editing a zone on a valid page.`);
            return;
           }
           if (!zoneName) {
            alert(`Unable to reset zone: Zone name is missing.`);
            return;
           }
           try {
            setIsResetting(true);
            const response = await platformApiService.getDefaultZoneTemplate(pageName, zoneName);
            const defaultTemplate = response.data?.zone;
            
            if (!defaultTemplate) {
             alert("Failed to load default template: No template returned");
             setHasTemplateContent(false);
             return;
            }
            
            // Only reset if template has content (button should only show if this is true)
            if (defaultTemplate.children && defaultTemplate.children.length > 0) {
             setLayout(defaultTemplate);
            } else {
             // Template is empty - hide reset button
             setHasTemplateContent(false);
             alert("The default template for this zone is empty. Reset is not available.");
            }
           } catch (error: any) {
            console.error("Error resetting zone:", error);
            const errorMessage = error.response?.data?.error || error.message || "Failed to reset zone";
            alert(`Failed to reset zone: ${errorMessage}`);
            setHasTemplateContent(false);
           } finally {
            setIsResetting(false);
           }
          } else if (pageType === "static" && pageName && hasTemplateContent) {
           // Static page or zone reset - only if template exists and has content
           // Reset in place without saving - user must save manually
           try {
            setIsResetting(true);
            let defaultTemplate: IComponentNode | null = null;
            
            if (zoneName) {
             // Zone reset for store admin
             const response = await api.get(`/page-builder/pages/${pageName}/zones/${zoneName}/default`);
             defaultTemplate = response.data?.zone;
            } else {
             // Static page reset
             const response = await api.get(`/page-builder/pages/${pageName}/default-layout`);
             defaultTemplate = response.data?.layout;
            }
            
            if (defaultTemplate && defaultTemplate.children && defaultTemplate.children.length > 0) {
             setLayout(defaultTemplate);
             // If onReset callback is provided, call it (for zone resets)
             if (onReset && zoneName) {
              await onReset(defaultTemplate);
             }
            } else {
             alert("The default template is empty. Reset is not available.");
             setHasTemplateContent(false);
            }
           } catch (error: any) {
            console.error("Error resetting:", error);
            if (error.response?.status === 404) {
             alert("No default template exists");
             setHasTemplateContent(false);
            } else {
             alert(error.response?.data?.error || "Failed to reset");
             setHasTemplateContent(false);
            }
           } finally {
            setIsResetting(false);
           }
          }
         }}
         variant="secondary"
         size="sm"
         className="text-red-600 hover:text-red-700 hover:bg-red-50"
         title={pageType === "platform" ? "Reset zone to default template" : "Reset page to default template"}
         disabled={isResetting}
        >
         {isResetting ? (
          <>
           <Spinner size="sm" className="text-red-600 mr-1" />
           Resetting...
          </>
         ) : (
          <>
           <FaUndo className="mr-1" />
           Reset
          </>
         )}
        </Button>
       ) : null}
      </div>
     </div>
    </div>

    {/* Canvas */}
    <div className="flex-1 overflow-auto bg-gray-50">
     <Canvas isPreviewMode={isPreviewMode} />
    </div>
   </div>

   {/* Property Drawer */}
   <PropertyDrawer isOpen={isPropertyDrawerOpen} onClose={() => setIsPropertyDrawerOpen(false)} />
  </div>
 );
};

export const PageBuilder: React.FC<PageBuilderProps> = ({
 initialLayout,
 onCancel,
 onSave,
 onReset,
 pageName,
 zoneName,
 pageType,
}) => {
 const [isPreviewMode, setIsPreviewMode] = useState(false);

 const sensors = useSensors(
  useSensor(PointerSensor, {
   activationConstraint: {
    distance: 8,
   },
  }),
  useSensor(KeyboardSensor)
 );

 return (
  <ModalLanguageProvider>
   <PageBuilderProvider initialLayout={initialLayout}>
    <DndContextWrapper sensors={sensors} isPreviewMode={isPreviewMode} setIsPreviewMode={setIsPreviewMode} onCancel={onCancel} onSave={onSave} onReset={onReset} pageName={pageName} zoneName={zoneName} pageType={pageType} />
   </PageBuilderProvider>
  </ModalLanguageProvider>
 );
};

const DndContextWrapper: React.FC<{
 sensors: ReturnType<typeof useSensors>;
 isPreviewMode: boolean;
 setIsPreviewMode: (mode: boolean) => void;
 onCancel?: () => void;
 onSave?: (layout: IComponentNode) => void | Promise<void>;
 onReset?: (defaultTemplate: IComponentNode) => void | Promise<void>;
 pageName?: string;
 zoneName?: string;
 pageType?: string;
}> = ({ sensors, isPreviewMode, setIsPreviewMode, onCancel, onSave, onReset, pageName, zoneName, pageType }) => {
 const { layout, updateComponent, addComponent, selectedComponent, setSelectedComponent, setLayout, findComponent } = usePageBuilder();
 const [activeId, setActiveId] = React.useState<string | null>(null);

 const handleDragStart = (event: any) => {
  setActiveId(event.active.id as string);
 };

 const handleDragEnd = (event: DragEndEvent) => {
  const { active, over } = event;
  setActiveId(null);

  if (!over || !layout) return;

  // Handle palette item drop
  if (active.id.toString().startsWith("palette-")) {
   const templateType = active.id.toString().replace("palette-", "");
   const template = componentTemplates.find((t) => t.type === templateType);
   if (!template) return;

   const newComponent: IComponentNode = {
    id: `${template.type}-${Date.now()}`,
    type: template.type,
    props: { ...template.defaultProps },
    styles: template.defaultStyles,
    children: [],
   };

   // Find drop target
   if (over.id.toString().startsWith("droppable-")) {
    const targetId = over.id.toString().replace("droppable-", "");
    
    // Check if dropping on root canvas
    if (targetId === "root-canvas") {
     // Add to root layout
     if (layout) {
      const newChildren = [...(layout.children || [])];
      newChildren.push(newComponent);
      updateComponent(layout.id, { children: newChildren });
     }
    } else {
     const findComponent = (node: IComponentNode, id: string): IComponentNode | null => {
      if (node.id === id) return node;
      if (node.children) {
       for (const child of node.children) {
        const found = findComponent(child, id);
        if (found) return found;
       }
      }
      return null;
     };

     const target = findComponent(layout, targetId);
     if (target) {
      const newChildren = [...(target.children || [])];
      newChildren.push(newComponent);
      updateComponent(target.id, { children: newChildren });
     }
    }
   } else if (over.id === "root-canvas") {
    // Drop directly on root canvas
    if (layout) {
     const newChildren = [...(layout.children || [])];
     newChildren.push(newComponent);
     updateComponent(layout.id, { children: newChildren });
    }
   } else {
    // Add to root or selected component
    const parentId = selectedComponent?.id || layout?.id || null;
    addComponent(parentId, newComponent);
   }
   return;
  }

  // Handle component reordering
  if (active.id === over.id) return;

  // Find the components involved
  const findComponent = (node: IComponentNode, id: string): { node: IComponentNode; parent: IComponentNode | null; index: number } | null => {
   // Check if this is the root node itself
   if (node.id === id) {
    return { node, parent: null, index: -1 };
   }
   
   if (node.children) {
    for (let i = 0; i < node.children.length; i++) {
     if (node.children[i].id === id) {
      return { node: node.children[i], parent: node, index: i };
     }
     const found = findComponent(node.children[i], id);
     if (found) return found;
    }
   }
   return null;
  };

  const activeComponent = findComponent(layout, active.id as string);
  if (!activeComponent) {
   // Component not found - this shouldn't happen, but if it does, just return
   // The drag state will be cleared and component should remain in place
   return;
  }
  
  // Don't allow dragging the root layout itself
  if (activeComponent.parent === null) {
   return;
  }

  // Check if dropping on droppable area
  if (over.id.toString().startsWith("droppable-")) {
   const targetId = over.id.toString().replace("droppable-", "");
   const findTarget = (node: IComponentNode, id: string): IComponentNode | null => {
    if (node.id === id) return node;
    if (node.children) {
     for (const child of node.children) {
      const found = findTarget(child, id);
      if (found) return found;
     }
    }
    return null;
   };

   const target = findTarget(layout, targetId);
   if (target && target.id !== activeComponent.parent?.id) {
    // Check if target is a descendant of the active component (prevent nesting inside itself)
    const isDescendant = (node: IComponentNode, ancestorId: string): boolean => {
     if (node.id === ancestorId) return true;
     if (node.children) {
      for (const child of node.children) {
       if (isDescendant(child, ancestorId)) return true;
      }
     }
     return false;
    };
    
    if (isDescendant(target, activeComponent.node.id)) {
     return; // Don't allow nesting inside itself
    }

    // Get the node data before moving (create a deep copy to avoid reference issues)
    const getNodeData = (node: IComponentNode): IComponentNode => {
     return {
      ...node,
      props: { ...node.props },
      styles: node.styles || { default: {} },
      children: node.children ? node.children.map(getNodeData) : [],
     };
    };
    const nodeToMove = getNodeData(activeComponent.node);

    // Move component in a single operation
    // Only create new references for components that actually changed (moved component and its parents)
    // Preserve references for unchanged components to avoid React reconciliation issues
    const moveComponent = (node: IComponentNode, path: string[] = []): IComponentNode => {
     const currentPath = [...path, node.id];
     const isRoot = node.id === layout.id;
     const isInPath = currentPath.includes(activeComponent.node.id) || 
                      currentPath.includes(activeComponent.parent?.id || '') ||
                      currentPath.includes(target.id);
     
     // Remove from old parent
     if (node.id === activeComponent.parent?.id && node.children) {
      const newChildren = node.children.filter((_, idx) => idx !== activeComponent.index);
      return { ...node, children: newChildren };
     }
     
     // Add to new parent
     if (node.id === target.id) {
      const newChildren = [...(node.children || [])];
      newChildren.push(nodeToMove);
      return { ...node, children: newChildren };
     }
     
     // Recursively update children - only create new references if in the affected path
     if (node.children) {
      const updatedChildren = node.children.map(child => moveComponent(child, currentPath));
      // Only create new object if children changed or if this node is in the affected path
      const childrenChanged = updatedChildren.some((child, idx) => child !== node.children![idx]);
      if (childrenChanged || isInPath || isRoot) {
       return {
        ...node,
        children: updatedChildren,
       };
      }
      // Preserve reference if nothing changed
      return node;
     }
     
     // If this is the root, always create a new reference to trigger re-render
     return isRoot ? { ...node } : node;
    };

    const updatedLayout = moveComponent(layout);
    // Always create a new root reference to ensure React detects the change
    const finalLayout = updatedLayout.id === layout.id 
     ? { ...updatedLayout } 
     : updatedLayout;
    
    // Verify the component still exists in the layout before updating
    const verifyComponentExists = (node: IComponentNode, componentId: string): boolean => {
     if (node.id === componentId) return true;
     if (node.children) {
      return node.children.some(child => verifyComponentExists(child, componentId));
     }
     return false;
    };
    
    const componentExists = verifyComponentExists(finalLayout, activeComponent.node.id);
    if (componentExists) {
     setLayout(finalLayout);
     // Select the moved component so it's visible and parent auto-expands
     const findMovedComponent = (node: IComponentNode): IComponentNode | null => {
      if (node.id === activeComponent.node.id) return node;
      if (node.children) {
       for (const child of node.children) {
        const found = findMovedComponent(child);
        if (found) return found;
       }
      }
      return null;
     };
     const movedComponent = findMovedComponent(finalLayout);
     if (movedComponent) {
      setSelectedComponent(movedComponent);
     }
    } else {
     // Component was lost during move - don't update layout, keep original
     console.warn("Component would be lost during move operation, cancelling");
    }
   }
   return;
  }

  const overComponent = findComponent(layout, over.id as string);
  if (!overComponent) {
   // If overComponent not found, check if dropping on root canvas
   // If so, add to root layout
   if (over.id === "root-canvas" && layout) {
    // Get the component data before moving
    const getNodeData = (node: IComponentNode): IComponentNode => {
     return {
      ...node,
      props: { ...node.props },
      styles: node.styles || { default: {} },
      children: node.children ? node.children.map(getNodeData) : [],
     };
    };
    const nodeToMove = getNodeData(activeComponent.node);

    // Move component to root in a single operation
    // Ensure we always create a new root reference for React to detect the change
    const moveToRoot = (node: IComponentNode): IComponentNode => {
     const isRoot = node.id === layout.id;
     
     // Remove from old parent
     if (node.id === activeComponent.parent?.id && node.children) {
      const newChildren = node.children.filter((_, idx) => idx !== activeComponent.index);
      return { ...node, children: newChildren };
     }
     
     // Add to root
     if (node.id === layout.id) {
      const newChildren = [...(node.children || [])];
      newChildren.push(nodeToMove);
      return { ...node, children: newChildren };
     }
     
     // Recursively update children
     if (node.children) {
      const updatedChildren = node.children.map(moveToRoot);
      return {
       ...node,
       children: updatedChildren,
      };
     }
     
     // If this is the root and nothing changed, still create a new reference
     return isRoot ? { ...node } : node;
    };

    const updatedLayout = moveToRoot(layout);
    // Always create a new root reference to ensure React detects the change
    const finalLayout = updatedLayout.id === layout.id 
     ? { ...updatedLayout } 
     : updatedLayout;
    
    // Verify the component still exists in the layout before updating
    const verifyComponentExists = (node: IComponentNode, componentId: string): boolean => {
     if (node.id === componentId) return true;
     if (node.children) {
      return node.children.some(child => verifyComponentExists(child, componentId));
     }
     return false;
    };
    
    const componentExists = verifyComponentExists(finalLayout, activeComponent.node.id);
    if (componentExists) {
     setLayout(finalLayout);
     // Select the moved component so it's visible and parent auto-expands
     const findMovedComponent = (node: IComponentNode): IComponentNode | null => {
      if (node.id === activeComponent.node.id) return node;
      if (node.children) {
       for (const child of node.children) {
        const found = findMovedComponent(child);
        if (found) return found;
       }
      }
      return null;
     };
     const movedComponent = findMovedComponent(finalLayout);
     if (movedComponent) {
      setSelectedComponent(movedComponent);
     }
    } else {
     // Component was lost during move - don't update layout, keep original
     console.warn("Component would be lost during move operation, cancelling");
    }
    return;
   }
   
   // If overComponent not found and not root canvas, the drag was cancelled or invalid
   // The component should remain in its original position
   // No action needed - the component will stay where it was
   return;
  }

  // If dragging within the same parent, reorder
  if (activeComponent.parent?.id === overComponent.parent?.id && activeComponent.parent) {
   const children = [...(activeComponent.parent.children || [])];
   const oldIndex = activeComponent.index;
   const newIndex = overComponent.index;
   
   // Only reorder if the index actually changed
   if (oldIndex !== newIndex) {
    const newChildren = arrayMove(children, oldIndex, newIndex);

    // Update the entire layout tree atomically to ensure components don't disappear
    // Ensure we always create a new root reference for React to detect the change
    const reorderInTree = (node: IComponentNode): IComponentNode => {
     const isRoot = node.id === layout.id;
     if (node.id === activeComponent.parent?.id) {
      return { ...node, children: newChildren };
     }
     if (node.children) {
      const updatedChildren = node.children.map(reorderInTree);
      return {
       ...node,
       children: updatedChildren,
      };
     }
     // If this is the root and nothing changed, still create a new reference
     return isRoot ? { ...node } : node;
    };

    const updatedLayout = reorderInTree(layout);
    // Always create a new root reference to ensure React detects the change
    const finalLayout = updatedLayout.id === layout.id 
     ? { ...updatedLayout } 
     : updatedLayout;
    // Select the moved component FIRST so it's available when the tree remounts
    const findMovedComponent = (node: IComponentNode): IComponentNode | null => {
     if (node.id === activeComponent.node.id) return node;
     if (node.children) {
      for (const child of node.children) {
       const found = findMovedComponent(child);
       if (found) return found;
      }
     }
     return null;
    };
    const movedComponent = findMovedComponent(finalLayout);
    if (movedComponent) {
     // Set selected component first, then layout, so the tree has the selected component when it remounts
     setSelectedComponent(movedComponent);
    }
    setLayout(finalLayout);
   }
  } else {
   // Dragging to a different parent - this should be handled by the droppable logic above
   // But if we get here, it means the drop target wasn't a droppable area
   // In this case, we should move the component to be a sibling of the over component
   if (overComponent.parent) {
    // Check if target is a descendant of the active component (prevent nesting inside itself)
    const isDescendant = (node: IComponentNode, ancestorId: string): boolean => {
     if (node.id === ancestorId) return true;
     if (node.children) {
      for (const child of node.children) {
       if (isDescendant(child, ancestorId)) return true;
      }
     }
     return false;
    };
    
    if (isDescendant(overComponent.node, activeComponent.node.id)) {
     // Don't allow nesting inside itself - cancel the move
     return;
    }

    // Remove from old parent
    const removeFromParent = (node: IComponentNode): IComponentNode => {
     if (node.id === activeComponent.parent?.id && node.children) {
      const newChildren = node.children.filter((_, idx) => idx !== activeComponent.index);
      return { ...node, children: newChildren };
     }
     if (node.children) {
      return {
       ...node,
       children: node.children.map(removeFromParent),
      };
     }
     return node;
    };

    // Add to new parent as sibling of over component
    const addToParent = (node: IComponentNode): IComponentNode => {
     if (node.id === overComponent.parent?.id && node.children) {
      const newChildren = [...node.children];
      const insertIndex = overComponent.index;
      // Get the component data
      const getNodeData = (node: IComponentNode): IComponentNode => {
       return {
        ...node,
        props: { ...node.props },
        styles: node.styles || { default: {} },
        children: node.children ? node.children.map(getNodeData) : [],
       };
      };
      const nodeToMove = getNodeData(activeComponent.node);
      newChildren.splice(insertIndex, 0, nodeToMove);
      return { ...node, children: newChildren };
     }
     if (node.children) {
      return {
       ...node,
       children: node.children.map(addToParent),
      };
     }
     return node;
    };

    // Apply both operations in a single pass
    // Ensure we always create a new root reference for React to detect the change
    const moveComponent = (node: IComponentNode): IComponentNode => {
     const isRoot = node.id === layout.id;
     let result = removeFromParent(node);
     result = addToParent(result);
     // Always create a new root reference to ensure React detects the change
     return isRoot ? { ...result } : result;
    };

    const updatedLayout = moveComponent(layout);
    // Always create a new root reference to ensure React detects the change
    const finalLayout = updatedLayout.id === layout.id 
     ? { ...updatedLayout } 
     : updatedLayout;
    
    // Verify the component still exists in the layout before updating
    const verifyComponentExists = (node: IComponentNode, componentId: string): boolean => {
     if (node.id === componentId) return true;
     if (node.children) {
      return node.children.some(child => verifyComponentExists(child, componentId));
     }
     return false;
    };
    
    const componentExists = verifyComponentExists(finalLayout, activeComponent.node.id);
    if (componentExists) {
     setLayout(finalLayout);
     // Select the moved component so it's visible and parent auto-expands
     const findMovedComponent = (node: IComponentNode): IComponentNode | null => {
      if (node.id === activeComponent.node.id) return node;
      if (node.children) {
       for (const child of node.children) {
        const found = findMovedComponent(child);
        if (found) return found;
       }
      }
      return null;
     };
     const movedComponent = findMovedComponent(finalLayout);
     if (movedComponent) {
      setSelectedComponent(movedComponent);
     }
    } else {
     // Component was lost during move - don't update layout, keep original
     console.warn("Component would be lost during move operation, cancelling");
    }
   }
  }
 };

 // Helper to get component label for drag overlay
 const getComponentLabelForOverlay = (componentId: string): string => {
  if (!layout) return "";
  const component = findComponent(componentId, layout);
  if (!component) return "";
  
  if (component.type === "heading" && component.props?.text) {
   return `Heading: ${component.props.text.substring(0, 30)}${component.props.text.length > 30 ? "..." : ""}`;
  }
  if (component.type === "text" && component.props?.text) {
   return `Text: ${component.props.text.substring(0, 30)}${component.props.text.length > 30 ? "..." : ""}`;
  }
  if (component.type === "image" && component.props?.alt) {
   return `Image: ${component.props.alt}`;
  }
  if (component.type === "icon" && component.props?.iconName) {
   return `Icon: ${component.props.iconName}`;
  }
  if (component.type === "container" && component.props?.className) {
   const classes = component.props.className.split(" ").slice(0, 2).join(" ");
   return `Container: ${classes || "default"}`;
  }
  return `${component.type.charAt(0).toUpperCase() + component.type.slice(1)}`;
 };

 // Helper to get component icon for drag overlay
 const getComponentIconForOverlay = (componentId: string) => {
  if (!layout) return null;
  const component = findComponent(componentId, layout);
  if (!component) return null;
  
  // Use simple colored squares for overlay - icons are imported in LayerTree
  const getColor = (type: string): string => {
   const colorMap: Record<string, string> = {
    container: "bg-blue-500",
    heading: "bg-purple-500",
    text: "bg-gray-500",
    image: "bg-green-500",
    button: "bg-blue-500",
    link: "bg-blue-500",
    grid: "bg-orange-500",
    list: "bg-teal-500",
    video: "bg-red-500",
    icon: "bg-pink-500",
    code: "bg-gray-600",
    quote: "bg-yellow-500",
   };
   return colorMap[type] || "bg-gray-400";
  };
  
  return <div className={`w-3 h-3 rounded ${getColor(component.type)}`} />;
 };

 return (
  <DndContext 
   sensors={sensors} 
   collisionDetection={closestCenter} 
   onDragEnd={handleDragEnd}
   onDragStart={handleDragStart}
   onDragOver={() => {}}
  >
   <PageBuilderContent isPreviewMode={isPreviewMode} setIsPreviewMode={setIsPreviewMode} onCancel={onCancel} onSave={onSave} onReset={onReset} pageName={pageName} zoneName={zoneName} pageType={pageType} />
   <DragOverlay>
    {activeId && !activeId.toString().startsWith("palette-") ? (
     <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded shadow-lg">
      <FaGripVertical className="text-gray-400" />
      {getComponentIconForOverlay(activeId)}
      <span className="text-sm text-gray-700">{getComponentLabelForOverlay(activeId)}</span>
     </div>
    ) : null}
   </DragOverlay>
  </DndContext>
 );
};

const SaveButtonWrapper: React.FC<{ onSave: (layout: IComponentNode) => void | Promise<void> }> = ({ onSave }) => {
 const { layout } = usePageBuilder();
 const [saving, setSaving] = React.useState(false);

 const handleSave = async (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  if (layout) {
   setSaving(true);
   try {
    await onSave(layout);
    // Small delay to show feedback
    setTimeout(() => {
     setSaving(false);
    }, 500);
   } catch (error) {
    console.error("Error saving:", error);
    setSaving(false);
   }
  }
 };

 return (
  <Button
   type="button"
   onClick={handleSave}
   variant="primary"
   size="sm"
   loading={saving}
   disabled={!layout}
  >
   Save
  </Button>
 );
};


